import { TFunction } from 'i18next'
import { Eye } from 'lucide-react'
import LoadIcons from '@/icons/LoadIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import {
    DataTableAction,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import { VCard } from '../types/response'
import { normalizeVCardUuid } from './vcard-public'

// ============================================================================
// Columns
// ============================================================================

export const getVCardListColumns = (
    t: TFunction,
    onNavigate: (uuid: string) => void
): DataTableColumn<VCard>[] => [
    {
        key: 'id',
        title: 'ID',
        width: 80,
        sortable: true,
        render: (value, row) => (
            <span
                className="font-medium text-primary underline cursor-pointer hover:opacity-80"
                onClick={() => onNavigate(normalizeVCardUuid(row.uuid))}
            >
                {value}
            </span>
        ),
    },
    {
        key: 'name',
        title: t('employee_name'),
        sortable: true,
    },
    {
        key: 'position',
        title: t('position'),
    },
    {
        key: 'email',
        title: 'Email',
    },
    {
        key: 'socials',
        title: t('socials'),
        render: (_value, row) => (
            <div className="flex items-center gap-2">
                {row?.socials?.map((item, index) => (
                    <span
                        key={index}
                        className="text-xs text-muted-foreground hover:text-primary cursor-pointer"
                        onClick={() => {
                            if (item?.value?.includes('http'))
                                window.open(item.value, '_blank')
                        }}
                    >
                        {item?.key}
                    </span>
                ))}
            </div>
        ),
    },
    {
        key: 'address',
        title: t('address'),
    },
]

// ============================================================================
// Actions
// ============================================================================

interface ActionsParams {
    t: TFunction
    navigate: (path: string) => void
    onDelete: (row: VCard) => void
    isDeleting: boolean
}

export const getVCardActions = ({
    t,
    navigate,
    onDelete,
    isDeleting,
}: ActionsParams): DataTableAction<VCard>[] => [
    {
        key: 'view',
        label: t('view'),
        icon: <Eye className="w-4 h-4" />,
        variant: 'info',
        onClick: (row) =>
            navigate(`/account/vcards/preview/${normalizeVCardUuid(row.uuid)}`),
    },
    {
        key: 'edit',
        label: t('edit'),
        icon: <LoadIcons className="w-4 h-4" />,
        variant: 'info',
        onClick: (row) => navigate(`/account/vcards/${row.id}/edit`),
    },
    {
        key: 'delete',
        label: t('delete'),
        icon: <DeleteOutlineIcons className="w-4 h-4" />,
        variant: 'danger',
        disabled: () => isDeleting,
        onClick: onDelete,
    },
]
